import axios from "axios";
import moment from 'moment';
import Notifications from 'react-notification-system-redux';
import { shapeData } from './common';
import * as constants from "./types";
import { fetchTransactionsWithRules } from './transactionHelpers';

export function clearStatus() {
  return { type: constants.COMPLETED, payload: false };
}

export function showError(message) {
  return Notifications.warning({ message });
}

// The list fetches previously had no timeout at all (axios defaults to 0, meaning
// wait forever), so a hung RAH call left the match modal spinning with no error.
// The match POSTs below are deliberately left without one: they can legitimately
// take minutes while RAH processes (see Api/RahApi.js, which allows 600s).
const LIST_TIMEOUT = 60000;

const listConfig = () => ({
  headers: { authorizationtoken: localStorage.getItem("token") },
  timeout: LIST_TIMEOUT
});

const listErrorMessage = (label, err) => (
  (err && err.code === 'ECONNABORTED') ? `Timed out fetching ${label}` : `Could not fetch ${label}`
);

// Reloads overlap in practice: a matchable list can take tens of seconds while the RAH
// queue is polled every few seconds, so a sync-triggered reload routinely starts before
// the previous one has finished. Every request carries an id and the reducer ignores any
// response that is not the newest for its list, so a slow older response cannot overwrite
// newer data or clear the loading flag while a newer request is still in flight.
let requestSequence = 0;

// Never rejects. Each list reports its own outcome against its own name, so the
// caller can settle several lists independently and a failure is attributed to the
// request that actually failed. `select` pulls the array out of the response body.
const fetchList = (dispatch, { list, label, url, select, shape = shapeMatchables, type }) => {
  requestSequence += 1;
  const requestId = requestSequence;
  dispatch({ type: constants.MATCH_LIST_START, list, requestId });

  return axios.get(url, listConfig())
    .then(response => {
      dispatch({ type, list, requestId, payload: shape(select(response)) });
    })
    .catch((err) => {
      if (process.env.BANKDATA_ENV !== 'production') {
        const errorMsg = `GET: ${url} -- Response: ${(err.response) ? JSON.stringify(err.response.data) : err.toString()}`;
        dispatch({ type: constants.SET_ERROR, payload: errorMsg });
      }
      dispatch({ type: constants.MATCH_LIST_ERROR, list, requestId, message: listErrorMessage(label, err) });
    });
};

export function loadData() {
    return function(dispatch) {
      const bookId = localStorage.getItem("bookid");

      // Undeposited items and invoices are fetched independently. They used to be
      // joined with Promise.all, which made either failure report as "Could not
      // fetch invoices" and withheld whichever list had already arrived.
      return Promise.all([
        fetchList(dispatch, {
          list: 'undeposited',
          label: 'undeposited items',
          url: `${constants.ROOT_URL}/RAH/${bookId}/undepositeditems`,
          select: response => response.data && response.data.undepositedItems,
          type: constants.GET_UNDEPOSTED
        }),
        fetchList(dispatch, {
          list: 'invoices',
          label: 'invoices',
          url: `${constants.ROOT_URL}/RAH/${bookId}/invoices`,
          select: response => response.data && response.data.invoices,
          type: constants.GET_INVOICES
        })
      ]);
    };
  }

export function getBills() {
    return function(dispatch) {
      return fetchList(dispatch, {
        list: 'bills',
        label: 'bills',
        url: `${constants.ROOT_URL}/RAH/${localStorage.getItem("bookid")}/bills`,
        select: response => response.data && response.data.bills,
        type: constants.GET_BILLS
      });
    };
  }

  // A missing or non-array payload yields an empty list rather than a TypeError out
  // of .reduce, which the caller would otherwise report as a failed fetch.
  const shapeMatchables = matchables => (
    (Array.isArray(matchables) ? matchables : []).reduce((filtered, matchable) => {
        if (matchable.balanceRemaining !== 0) {
            // undeposited items does not have balanceRemaining, so assign amount instead.
            const balanceRemaining = matchable.hasOwnProperty('balanceRemaining') ? matchable.balanceRemaining : matchable.amount;
            matchable = {
                txnId: matchable.txnId,
                date: matchable.date, 
                type: matchable.txnType, 
                reference: matchable.reference,
                contactId: matchable.contactId,
                amount: matchable.amount,
                balanceRemaining,
                payment: 0
            }
            filtered.push(matchable)
        }
        return filtered;
     }, [])
  );

  export function deposit(paymentDetails, BankDataGUID, dateRange, account = undefined) {

    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");

    return function(dispatch) {
        dispatch({ type: constants.POSTING });
        dispatch({ type: constants.START_MATCH_LOADING, payload: BankDataGUID });
        axios.post(`${constants.ROOT_URL}/RAH/${bookId}/deposit/${BankDataGUID}`, paymentDetails, header)
        .then(response => {
              dispatch({ type: constants.COMPLETED, payload: true });
              dispatch(Notifications.success({ message: 'Match success' }));

              if (account) {
                const headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype"), lastgetelapsed: 20 };
                fetchTransactionsWithRules(bookId, dateRange, account, headers)
                  .then(data => {
                    const transactionLength = data ? data.transactions.length : 0;
                    const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];
                    dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  })
                  .catch(() => {
                    dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
                  });
              }
      })
      .catch(() => {
        dispatch({ type: constants.COMPLETED, payload: false });
        dispatch({ type: constants.STOP_MATCH_LOADING, payload: BankDataGUID });
        dispatch(Notifications.warning({ message: 'Unable to match' }));
      });
    };
  }

  export function receivePayment(paymentDetails, BankDataGUID, dateRange, account = undefined) {

    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");

    return function(dispatch) {
        dispatch({ type: constants.POSTING });
        dispatch({ type: constants.START_MATCH_LOADING, payload: BankDataGUID });
        axios.post(`${constants.ROOT_URL}/RAH/${bookId}/receivepayment/${BankDataGUID}`, paymentDetails, header)
        .then(response => {
              dispatch({ type: constants.COMPLETED, payload: true });
              dispatch(Notifications.success({ message: 'Match success' }));

              if (account) {
                const headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype"), lastgetelapsed: 20 };
                fetchTransactionsWithRules(bookId, dateRange, account, headers)
                  .then(data => {
                    const transactionLength = data ? data.transactions.length : 0;
                    const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];
                    dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  })
                  .catch(() => {
                    dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
                  });
              }
      })
      .catch(() => {
        dispatch({ type: constants.COMPLETED, payload: false });
        dispatch({ type: constants.STOP_MATCH_LOADING, payload: BankDataGUID });
        dispatch(Notifications.warning({ message: 'Unable to match invoice' }));
      });
    };
  }

  export function billPayment(paymentDetails, BankDataGUID, dateRange, account = undefined) {

    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");

    return function(dispatch) {
        dispatch({ type: constants.POSTING });
        dispatch({ type: constants.START_MATCH_LOADING, payload: BankDataGUID });
        axios.post(`${constants.ROOT_URL}/RAH/${bookId}/billPayment/${BankDataGUID}`, paymentDetails, header)
        .then(response => {
              dispatch({ type: constants.COMPLETED, payload: true });
              dispatch(Notifications.success({ message: 'Match success' }));

              if (account) {
                const headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype"), lastgetelapsed: 20 };
                fetchTransactionsWithRules(bookId, dateRange, account, headers)
                  .then(data => {
                    const transactionLength = data ? data.transactions.length : 0;
                    const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];
                    dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  })
                  .catch(() => {
                    dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
                  });
              }
      })
      .catch(() => {
        dispatch({ type: constants.COMPLETED, payload: false });
        dispatch({ type: constants.STOP_MATCH_LOADING, payload: BankDataGUID });
        dispatch(Notifications.warning({ message: 'Unable to match bill' }));
      });
    };
  }

  export function deleteMatchItem(transaction) {
    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");
    return function(dispatch) {
      dispatch({ type: constants.IS_SAVING, payload: transaction.BankDataGUID });
      dispatch({ type: constants.STOP_MATCH_LOADING, payload: transaction.BankDataGUID });
      axios.post(`${constants.ROOT_URL}/RAH/${bookId}/match/${transaction.BankDataGUID}`, { MatchId: null },
      { headers: { authorizationtoken: localStorage.getItem("token") } })
        .finally(() => dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID }))
        .then(() => {
            axios.delete(`${constants.ROOT_URL}/RAH/${bookId}/item/${transaction.MatchId}`, header)
            .then(() => {
              const newTransaction = { ...transaction, MatchId: null, SuggestedMatches: transaction.SuggestedMatches.filter(matchId => matchId !== transaction.MatchId) }
              dispatch({ type: constants.UPDATE_TRANSACTIONS, payload: newTransaction });
              dispatch(Notifications.success({ message: 'Match deleted' }));
            })
            .catch(() => {
              const newTransaction = { ...transaction, MatchId: null }  
              dispatch({ type: constants.UPDATE_TRANSACTIONS, payload: newTransaction });
              dispatch(Notifications.warning({ message: 'Unmatched, but failed to delete match' }));
            });
        })
        .catch((error) => {
          if (process.env.BANKDATA_ENV === 'production') {
            dispatch(Notifications.warning({ message: 'Could not unmatch' }));
          } else {
            const errorMsg = `${constants.ROOT_URL}/RAH/${bookId}/match/${transaction.BankDataGUID} -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
            dispatch({ type: constants.SET_ERROR, payload: errorMsg });
          }
        });
    };
  }

  // ---- R1 Matching ----

  const shapeR1Invoices = (invoices) =>
    (Array.isArray(invoices) ? invoices : []).reduce((filtered, inv) => {
      const paidAmount = (inv.transactionLinks || []).reduce((sum, link) => sum + (link.transactionAmount || 0), 0);
      const balanceRemaining = Number((inv.totalAmount - paidAmount).toFixed(2));
      if (balanceRemaining !== 0) {
        filtered.push({
          txnId: inv.id,
          date: inv.invoiceDate,
          type: 'Invoice',
          reference: inv.invoiceNumber,
          contactId: inv.customer ? inv.customer.id : null,
          contactName: inv.customer ? inv.customer.name : '',
          amount: inv.totalAmount,
          balanceRemaining,
          payment: 0
        });
      }
      return filtered;
    }, []);

  const shapeR1Bills = (bills) =>
    (Array.isArray(bills) ? bills : []).reduce((filtered, bill) => {
      const paidAmount = (bill.transactionLinks || []).reduce((sum, link) => sum + (link.transactionAmount || 0), 0);
      const balanceRemaining = Number((bill.totalAmount - paidAmount).toFixed(2));
      if (balanceRemaining !== 0) {
        filtered.push({
          txnId: bill.id,
          date: bill.billDate,
          type: 'Bill',
          reference: bill.billNumber,
          contactId: bill.supplier ? bill.supplier.id : null,
          contactName: bill.supplier ? bill.supplier.name : '',
          amount: bill.totalAmount,
          balanceRemaining,
          payment: 0
        });
      }
      return filtered;
    }, []);

  export function loadR1Data(dateRange) {
    return function(dispatch) {
      const bookId = localStorage.getItem("bookid");
      const dateFrom = dateRange.transactionsFrom.format('YYYY-MM-DD');
      const dateTo = dateRange.transactionsTo.format('YYYY-MM-DD');

      return fetchList(dispatch, {
        list: 'invoices',
        label: 'invoices',
        url: `${constants.ROOT_URL}/R1/${bookId}/invoices?dateFrom=${dateFrom}&dateTo=${dateTo}`,
        select: response => response.data && response.data.invoices,
        shape: shapeR1Invoices,
        type: constants.GET_INVOICES
      });
    };
  }

  export function getR1Bills(dateRange) {
    return function(dispatch) {
      const bookId = localStorage.getItem("bookid");
      const dateFrom = dateRange.transactionsFrom.format('YYYY-MM-DD');
      const dateTo = dateRange.transactionsTo.format('YYYY-MM-DD');

      return fetchList(dispatch, {
        list: 'bills',
        label: 'bills',
        url: `${constants.ROOT_URL}/R1/${bookId}/bills?dateFrom=${dateFrom}&dateTo=${dateTo}`,
        select: response => response.data && response.data.bills,
        shape: shapeR1Bills,
        type: constants.GET_BILLS
      });
    };
  }

  export function r1BillPayment(paymentDetails, BankDataGUID, dateRange, account = undefined) {
    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");

    return function(dispatch) {
        dispatch({ type: constants.POSTING });
        dispatch({ type: constants.START_MATCH_LOADING, payload: BankDataGUID });
        axios.post(`${constants.ROOT_URL}/R1/${bookId}/billPayment/${BankDataGUID}`, paymentDetails, header)
        .then(response => {
              dispatch({ type: constants.COMPLETED, payload: true });
              dispatch(Notifications.success({ message: 'Match success' }));

              if (account) {
                const headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype"), lastgetelapsed: 20 };
                fetchTransactionsWithRules(bookId, dateRange, account, headers)
                  .then(data => {
                    const transactionLength = data ? data.transactions.length : 0;
                    const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];
                    dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  })
                  .catch(() => {
                    dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
                  });
              }
      })
      .catch(() => {
        dispatch({ type: constants.COMPLETED, payload: false });
        dispatch({ type: constants.STOP_MATCH_LOADING, payload: BankDataGUID });
        dispatch(Notifications.warning({ message: 'Unable to match bill' }));
      });
    };
  }

  export function r1ReceivePayment(paymentDetails, BankDataGUID, dateRange, account = undefined) {
    const header = { headers: { authorizationtoken: localStorage.getItem("token") } };
    const bookId = localStorage.getItem("bookid");

    return function(dispatch) {
        dispatch({ type: constants.POSTING });
        dispatch({ type: constants.START_MATCH_LOADING, payload: BankDataGUID });
        axios.post(`${constants.ROOT_URL}/R1/${bookId}/receivePayment/${BankDataGUID}`, paymentDetails, header)
        .then(response => {
              dispatch({ type: constants.COMPLETED, payload: true });
              dispatch(Notifications.success({ message: 'Match success' }));

              if (account) {
                const headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype"), lastgetelapsed: 20 };
                fetchTransactionsWithRules(bookId, dateRange, account, headers)
                  .then(data => {
                    const transactionLength = data ? data.transactions.length : 0;
                    const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];
                    dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  })
                  .catch(() => {
                    dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
                  });
              }
      })
      .catch(() => {
        dispatch({ type: constants.COMPLETED, payload: false });
        dispatch({ type: constants.STOP_MATCH_LOADING, payload: BankDataGUID });
        dispatch(Notifications.warning({ message: 'Unable to match invoice' }));
      });
    };
  }

  export function clearMatchables() {
    return { type: constants.CLEAR_MATCH };
  }



// WEBPACK FOOTER //
// ./src/Actions/matchables.js